import { useEffect, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import {
  AlertTriangle,
  Camera,
  CheckCircle2,
  ChevronDown,
  Circle,
  ClipboardCheck,
  Clock,
  Lock,
  Pencil,
  Plus,
  Trash2,
  X,
} from 'lucide-react';
import { formatDate, type Inspection, type InspectionRoom, type Lease } from '@vuokramani/shared';
import { Section, pageStyles as ps } from '../../components/page.tsx';
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  EmptyState,
  Panel,
  Splash,
  TextArea,
} from '../../components/ui.tsx';
import { ApiError } from '../../lib/api.ts';
import {
  photoUrl,
  useInspectionActions,
  useInspections,
  useUploadPhotos,
} from '../../lib/inspections.ts';
import s from './inspection.module.css';

function useDateTime() {
  const { i18n } = useTranslation();
  const fmt = new Intl.DateTimeFormat(i18n.language, {
    dateStyle: 'short',
    timeStyle: 'short',
    timeZone: 'Europe/Helsinki',
  });
  return (iso: string) => fmt.format(new Date(iso));
}

/**
 * Vuokrasuhteen tarkastukset. Irtisanomisen jälkeen ylimpänä on lopputarkastus. Jos
 * muuttotarkastus on tehty muualla (vanha vuokrasuhde), sitä ei pyydetä sovelluksessa.
 */
export function LeaseInspections({ lease }: { lease: Lease }) {
  const { t } = useTranslation();
  const moveIn =
    lease.moveInInspection === 'external' ? (
      <p className={s.external}>{t('inspection.external')}</p>
    ) : (
      <InspectionView lease={lease} type="move_in" />
    );
  if (!lease.terminatedBy) return moveIn;
  return (
    <div className={ps.stack}>
      <Section title={t('inspection.moveOutTitle')} note={t('inspection.moveOutNote')}>
        <InspectionView lease={lease} type="move_out" />
      </Section>
      <Section title={t('inspection.moveInTitle')}>{moveIn}</Section>
    </div>
  );
}

/**
 * Muutto- tai lopputarkastus huone kerrallaan. Sama näkymä vuokranantajalle ja
 * vuokralaiselle; painikkeet riippuvat roolista ja tilasta:
 *   muuttotarkastus: luonnos → vuokralainen lähettää → vuokranantaja hyväksyy tai palauttaa → lukittu
 *   lopputarkastus: vuokranantaja tekee ja merkitsee valmiiksi → vuokralainen näkee sen.
 */
export function InspectionView({
  lease,
  type = 'move_in',
}: {
  lease: Lease;
  type?: 'move_in' | 'move_out';
}) {
  const { t } = useTranslation();
  const { data: inspections, isLoading } = useInspections(lease.id);
  const actions = useInspectionActions(lease.id);
  const inspection = inspections?.find((i) => i.type === type);
  const out = type === 'move_out';
  // Vain luettavissa: päättynyt vuokrasuhde (esim. vertailuksi seuraavaan), tai vuokralainen,
  // kun vuokranantaja tekee tarkastuksen (aina lopputarkastus, muuttotarkastus asetuksen mukaan).
  const isTenant = lease.parties.some((x) => x.isMe);
  const ended = lease.status === 'ended' || (isTenant && (out || !lease.tenantInspections));
  // Vuokranantaja täyttää ja kuittaa itse.
  const noTenantInApp =
    out || !lease.tenantInspections || !lease.parties.some((x) => x.access === 'joined');
  // Kun molemmat ovat kuitanneet, huoneet ja kuvat ovat arkistoa: piilossa, avattavissa.
  const [showRooms, setShowRooms] = useState(false);

  if (isLoading) return <Splash />;
  if (!inspection) {
    return (
      <EmptyState
        icon={<ClipboardCheck size={22} />}
        title={out ? t('inspection.outNotStarted') : t('inspection.notStarted')}
        text={
          ended
            ? lease.status === 'ended'
              ? t('inspection.notDone')
              : out
                ? t('inspection.outTenantWaits')
                : t('inspection.landlordDoes')
            : out
              ? t('inspection.outNotStartedText')
              : t('inspection.notStartedText')
        }
        action={
          ended ? undefined : (
            <Button
              variant="primary"
              disabled={actions.start.isPending}
              onClick={() => actions.start.mutate(type)}
            >
              {out ? t('inspection.outStart') : t('inspection.start')}
            </Button>
          )
        }
      />
    );
  }

  const editable = inspection.status === 'draft' && !ended;
  const collapsible = inspection.status === 'approved';
  const photoCount = inspection.rooms.reduce((n, r) => n + r.photos.length, 0);
  const roomsId = `inspection-rooms-${inspection.id}`;
  return (
    <>
      <StatusCard
        inspection={inspection}
        noTenantInApp={noTenantInApp}
        footer={
          collapsible && (
            <button
              type="button"
              className={s.roomsToggle}
              aria-expanded={showRooms}
              aria-controls={roomsId}
              onClick={() => setShowRooms(!showRooms)}
            >
              <span>
                <strong>{t('inspection.roomsAndPhotos')}</strong>{' '}
                <span className={s.roomsCount}>
                  {t('inspection.roomCount', { count: inspection.rooms.length })},{' '}
                  {t('inspection.photoCount', { count: photoCount })}
                </span>
              </span>
              <span className={s.roomsToggleAction}>
                {showRooms ? t('inspection.hideRooms') : t('inspection.showRooms')}
                <ChevronDown
                  size={18}
                  aria-hidden="true"
                  className={showRooms ? s.chevronOpen : undefined}
                />
              </span>
            </button>
          )
        }
      />
      {(!collapsible || showRooms) && (
        <div id={roomsId} className={s.rooms}>
          {inspection.rooms.map((room) => (
            <RoomCard key={room.id} leaseId={lease.id} room={room} editable={editable} />
          ))}
          {editable && <AddRoom leaseId={lease.id} inspectionId={inspection.id} />}
        </div>
      )}
      {!ended && (
        <ActionBar leaseId={lease.id} inspection={inspection} noTenantInApp={noTenantInApp} />
      )}
      <Notes leaseId={lease.id} inspection={inspection} readOnly={ended} />
    </>
  );
}

function StatusCard({
  inspection: i,
  noTenantInApp,
  footer,
}: {
  inspection: Inspection;
  noTenantInApp: boolean;
  footer?: ReactNode;
}) {
  const { t, i18n } = useTranslation();
  const dateTime = useDateTime();
  const Icon = i.status === 'approved' ? Lock : i.status === 'submitted' ? Clock : ClipboardCheck;
  const out = i.type === 'move_out';
  const text =
    i.status === 'draft'
      ? noTenantInApp && i.myAccess === 'owner'
        ? out
          ? t('inspection.outSelfDraftText')
          : t('inspection.selfDraftText')
        : i.dueDate
          ? t('inspection.dueBy', { date: formatDate(i.dueDate, i18n.language) })
          : t('inspection.draftText')
      : i.status === 'submitted'
        ? t('inspection.submittedText')
        : i.submittedAt
          ? t('inspection.approvedText')
          : out
            ? t('inspection.outApprovedText')
            : t('inspection.approvedSelfText');

  return (
    <section className={`${s.status} ${s[i.status]}`} aria-label={t('inspection.statusLabel')}>
      <div className={s.statusHead}>
        <span className={s.statusIcon} aria-hidden="true">
          <Icon size={20} />
        </span>
        <div>
          <div className={s.statusTitle}>{t(`inspection.status.${i.status}`)}</div>
          <div className={s.statusText}>{text}</div>
        </div>
      </div>
      <div className={s.signoffs}>
        {/* Lopputarkastuksen tekee vuokranantaja: vuokralaisen kuittausta ei pyydetä. */}
        {(!out || i.submittedAt) && (
          <Signoff
            done={Boolean(i.submittedAt) && i.status !== 'draft'}
            title={t('inspection.tenantSignoff')}
            detail={
              i.submittedAt && i.status !== 'draft'
                ? `${dateTime(i.submittedAt)}${i.submittedByName ? `, ${i.submittedByName}` : ''}`
                : i.status === 'approved'
                  ? t('inspection.tenantSkipped')
                  : noTenantInApp
                    ? t('inspection.tenantNotInApp')
                    : t('inspection.notYet')
            }
          />
        )}
        <Signoff
          done={i.status === 'approved'}
          title={t('inspection.landlordSignoff')}
          detail={
            i.approvedAt
              ? `${dateTime(i.approvedAt)}${i.approvedByName ? `, ${i.approvedByName}` : ''}`
              : t('inspection.notYet')
          }
        />
      </div>
      {i.status === 'draft' && i.returnComment && (
        <p className={s.returned} role="status">
          <AlertTriangle size={16} aria-hidden="true" />
          <span>
            <strong>{t('inspection.returnedTitle')}</strong> {i.returnComment}
          </span>
        </p>
      )}
      {footer}
    </section>
  );
}

function Signoff({ done, title, detail }: { done: boolean; title: string; detail: string }) {
  return (
    <div className={`${s.signoff} ${done ? s.signoffDone : ''}`}>
      {done ? (
        <CheckCircle2 size={18} aria-hidden="true" />
      ) : (
        <Circle size={18} aria-hidden="true" />
      )}
      <span className={s.signoffText}>
        <strong>{title}</strong>
        <span>{detail}</span>
      </span>
    </div>
  );
}

function RoomCard({
  leaseId,
  room,
  editable,
}: {
  leaseId: string;
  room: InspectionRoom;
  editable: boolean;
}) {
  const { t } = useTranslation();
  const dateTime = useDateTime();
  const actions = useInspectionActions(leaseId);
  const upload = useUploadPhotos(leaseId);
  const [note, setNote] = useState(room.note ?? '');
  const [saved, setSaved] = useState(false);
  const [renaming, setRenaming] = useState(false);
  const [name, setName] = useState(room.name);
  const [progress, setProgress] = useState<string>();

  useEffect(() => setNote(room.note ?? ''), [room.note]);

  function saveNote() {
    if ((room.note ?? '') === note.trim()) return;
    actions.updateRoom.mutate(
      { id: room.id, note: note.trim() || null },
      {
        onSuccess: () => {
          setSaved(true);
          setTimeout(() => setSaved(false), 2000);
        },
      },
    );
  }

  function choose(list: FileList | null) {
    const files = Array.from(list ?? []);
    if (files.length === 0) return;
    upload.mutate(
      {
        roomId: room.id,
        files,
        onProgress: (done, total) =>
          setProgress(done < total ? t('inspection.uploading', { n: done + 1, total }) : undefined),
      },
      { onSettled: () => setProgress(undefined) },
    );
  }

  const uploadError =
    upload.error instanceof ApiError && upload.error.code === 'file_type'
      ? t('inspection.wrongType', { name: upload.error.message })
      : upload.isError
        ? t('errors.generic')
        : undefined;

  return (
    <article className={s.room}>
      <div className={s.roomHead}>
        {renaming ? (
          <form
            style={{ display: 'flex', gap: 8, flex: 1 }}
            onSubmit={(e) => {
              e.preventDefault();
              if (name.trim()) actions.updateRoom.mutate({ id: room.id, name: name.trim() });
              setRenaming(false);
            }}
          >
            <input
              className={`${s.note} ${s.nameInput}`}
              style={{ minHeight: 40 }}
              value={name}
              aria-label={t('inspection.roomName')}
              autoFocus
              onChange={(e) => setName(e.target.value)}
            />
            <Button type="submit" variant="primary">
              {t('common.save')}
            </Button>
          </form>
        ) : (
          <h3 className={s.roomName}>{room.name}</h3>
        )}
        {editable && !renaming && (
          <span className={s.roomTools}>
            <Button
              variant="ghost"
              icon
              aria-label={t('inspection.rename')}
              title={t('inspection.rename')}
              onClick={() => setRenaming(true)}
            >
              <Pencil size={15} />
            </Button>
            {room.photos.length === 0 && (
              <Button
                variant="ghost"
                icon
                aria-label={t('inspection.deleteRoom')}
                title={t('inspection.deleteRoom')}
                onClick={() => actions.deleteRoom.mutate(room.id)}
              >
                <Trash2 size={15} />
              </Button>
            )}
          </span>
        )}
      </div>

      {editable ? (
        <div>
          <textarea
            className={s.note}
            value={note}
            placeholder={t('inspection.notePlaceholder')}
            aria-label={t('inspection.noteLabel', { room: room.name })}
            onChange={(e) => setNote(e.target.value)}
            onBlur={saveNote}
          />
          <div className={s.saved} aria-live="polite">
            {saved ? t('inspection.saved') : ''}
          </div>
        </div>
      ) : room.note ? (
        <p className={s.noteText}>{room.note}</p>
      ) : (
        <p className={s.noteEmpty}>{t('inspection.noNote')}</p>
      )}

      {(room.photos.length > 0 || editable) && (
        <div className={s.photos}>
          {room.photos.map((p) => (
            <figure key={p.id} className={s.photo}>
              <a href={photoUrl(p.id)} target="_blank" rel="noopener">
                <img src={photoUrl(p.id)} alt={`${room.name}: ${p.name}`} loading="lazy" />
              </a>
              <figcaption className={s.photoMeta}>
                {t('inspection.uploadedAt', { when: dateTime(p.uploadedAt) })}
                {p.capturedAt && (
                  <>
                    <br />
                    {t('inspection.capturedAt', { when: dateTime(p.capturedAt) })}
                  </>
                )}
              </figcaption>
              {editable && p.uploadedByMe && (
                <button
                  type="button"
                  className={s.photoDelete}
                  aria-label={t('inspection.deletePhoto')}
                  title={t('inspection.deletePhoto')}
                  onClick={() => actions.deletePhoto.mutate(p.id)}
                >
                  <X size={16} />
                </button>
              )}
            </figure>
          ))}
          {editable && (
            <label className={s.addPhoto}>
              <Camera size={22} aria-hidden="true" />
              <span>{progress ?? t('inspection.addPhoto')}</span>
              {/* Puhelimessa tarjoaa kameran ja gallerian; ei capture-attribuuttia, jotta molemmat käyvät */}
              <input
                type="file"
                accept="image/*"
                multiple
                className="visually-hidden"
                disabled={upload.isPending}
                onChange={(e) => {
                  choose(e.target.files);
                  e.target.value = '';
                }}
              />
            </label>
          )}
        </div>
      )}
      {uploadError && <Alert>{uploadError}</Alert>}
    </article>
  );
}

function AddRoom({ leaseId, inspectionId }: { leaseId: string; inspectionId: string }) {
  const { t } = useTranslation();
  const actions = useInspectionActions(leaseId);
  const [open, setOpen] = useState(false);
  const [name, setName] = useState('');
  if (!open) {
    return (
      <Button className={s.addRoom} onClick={() => setOpen(true)}>
        <Plus size={16} aria-hidden="true" />
        {t('inspection.addRoom')}
      </Button>
    );
  }
  return (
    <form
      style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}
      onSubmit={(e) => {
        e.preventDefault();
        if (!name.trim()) return;
        actions.addRoom.mutate(
          { id: inspectionId, name: name.trim() },
          {
            onSuccess: () => {
              setName('');
              setOpen(false);
            },
          },
        );
      }}
    >
      <input
        className={s.note}
        style={{ minHeight: 42, maxWidth: 280 }}
        placeholder={t('inspection.roomPlaceholder')}
        aria-label={t('inspection.roomName')}
        value={name}
        autoFocus
        onChange={(e) => setName(e.target.value)}
      />
      <Button type="submit" variant="primary">
        {t('inspection.addRoom')}
      </Button>
      <Button variant="ghost" onClick={() => setOpen(false)}>
        {t('common.cancel')}
      </Button>
    </form>
  );
}

function ActionBar({
  leaseId,
  inspection: i,
  noTenantInApp,
}: {
  leaseId: string;
  inspection: Inspection;
  noTenantInApp: boolean;
}) {
  const { t, i18n } = useTranslation();
  const actions = useInspectionActions(leaseId);
  const [confirm, setConfirm] = useState<'submit' | 'approve' | 'return' | null>(null);
  const [comment, setComment] = useState('');
  const isTenant = i.myAccess === 'party';
  const out = i.type === 'move_out';

  if (i.status === 'approved') return null;

  let text: string;
  let buttons: ReactNode = null;
  if (i.status === 'draft' && isTenant) {
    text = t('inspection.submitHelp');
    buttons = (
      <Button variant="primary" onClick={() => setConfirm('submit')}>
        {t('inspection.submit')}
      </Button>
    );
  } else if (i.status === 'draft' && noTenantInApp) {
    // Lopputarkastus, tai vuokralainen ei tee muuttotarkastusta: vuokranantaja kuittaa itse.
    text = out ? t('inspection.outCompleteHelp') : t('inspection.selfApproveHelp');
    buttons = (
      <Button variant="primary" onClick={() => setConfirm('approve')}>
        {out ? t('inspection.outComplete') : t('inspection.approve')}
      </Button>
    );
  } else if (i.status === 'draft') {
    text = i.dueDate
      ? t('inspection.waitingTenantDue', { date: formatDate(i.dueDate, i18n.language) })
      : t('inspection.waitingTenant');
  } else if (isTenant) {
    text = t('inspection.waitingLandlord');
  } else {
    text = t('inspection.reviewHelp');
    buttons = (
      <>
        <Button onClick={() => setConfirm('return')}>{t('inspection.giveBack')}</Button>
        <Button variant="primary" onClick={() => setConfirm('approve')}>
          {t('inspection.approve')}
        </Button>
      </>
    );
  }

  const busy = actions.submit.isPending || actions.approve.isPending || actions.giveBack.isPending;
  const close = () => setConfirm(null);

  return (
    <>
      <div className={s.actions}>
        <p className={s.actionsText}>{text}</p>
        {buttons && <div className={s.actionsButtons}>{buttons}</div>}
      </div>

      <Dialog open={confirm === 'submit'} onClose={close} title={t('inspection.submitTitle')}>
        <p>{t('inspection.submitConfirm')}</p>
        <DialogActions>
          <Button variant="ghost" onClick={close}>
            {t('common.cancel')}
          </Button>
          <Button
            variant="primary"
            disabled={busy}
            onClick={() => actions.submit.mutate(i.id, { onSuccess: close })}
          >
            {t('inspection.submit')}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={confirm === 'approve'}
        onClose={close}
        title={out ? t('inspection.outCompleteTitle') : t('inspection.approveTitle')}
      >
        <p>{out ? t('inspection.outCompleteConfirm') : t('inspection.approveConfirm')}</p>
        <DialogActions>
          <Button variant="ghost" onClick={close}>
            {t('common.cancel')}
          </Button>
          <Button
            variant="primary"
            disabled={busy}
            onClick={() => actions.approve.mutate(i.id, { onSuccess: close })}
          >
            {out ? t('inspection.outComplete') : t('inspection.approve')}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={confirm === 'return'} onClose={close} title={t('inspection.giveBackTitle')}>
        <form
          style={{ display: 'grid', gap: '1rem' }}
          onSubmit={(e) => {
            e.preventDefault();
            if (!comment.trim()) return;
            actions.giveBack.mutate(
              { id: i.id, comment: comment.trim() },
              {
                onSuccess: () => {
                  setComment('');
                  close();
                },
              },
            );
          }}
        >
          <TextArea
            label={t('inspection.giveBackLabel')}
            hint={t('inspection.giveBackHint')}
            value={comment}
            onChange={(e) => setComment(e.target.value)}
          />
          <DialogActions>
            <Button variant="ghost" onClick={close}>
              {t('common.cancel')}
            </Button>
            <Button type="submit" variant="primary" disabled={busy || !comment.trim()}>
              {t('inspection.giveBack')}
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </>
  );
}

/** Lisähuomiot lukituksen jälkeen: päivättyjä, ei muokata eikä poisteta. */
function Notes({
  leaseId,
  inspection: i,
  readOnly = false,
}: {
  leaseId: string;
  inspection: Inspection;
  readOnly?: boolean;
}) {
  const { t } = useTranslation();
  const dateTime = useDateTime();
  const actions = useInspectionActions(leaseId);
  const [text, setText] = useState('');
  if ((readOnly || i.status !== 'approved') && i.notes.length === 0) return null;
  return (
    <div style={{ marginTop: '2rem' }}>
      <Section title={t('inspection.notesTitle')} note={t('inspection.notesHelp')}>
        <Panel>
          {i.notes.length > 0 && (
            <ul className={s.notes}>
              {i.notes.map((n) => (
                <li key={n.id} className={s.noteItem}>
                  <div className={s.noteMeta}>
                    {dateTime(n.createdAt)}, {n.byMe ? t('lease.you') : (n.authorName ?? '')}
                  </div>
                  <div className={s.noteText}>{n.text}</div>
                </li>
              ))}
            </ul>
          )}
          {!readOnly && (
            <form
              style={{ display: 'grid', gap: '0.75rem' }}
              onSubmit={(e) => {
                e.preventDefault();
                if (!text.trim()) return;
                actions.addNote.mutate(
                  { id: i.id, text: text.trim() },
                  { onSuccess: () => setText('') },
                );
              }}
            >
              <TextArea
                label={t('inspection.addNoteLabel')}
                value={text}
                onChange={(e) => setText(e.target.value)}
              />
              <div>
                <Button type="submit" disabled={!text.trim() || actions.addNote.isPending}>
                  {t('inspection.addNote')}
                </Button>
              </div>
            </form>
          )}
        </Panel>
      </Section>
    </div>
  );
}
